import { useCallback, useState } from "react"
import { useToast } from "@mallardbay/lib-react-components"

import {
    isSupportedAiChatAttachmentMediaType,
    resolvePastedFileMediaType,
    type ChatUploadedFile,
} from "~components/shared/chat/chat-file-attachment-helpers"
import COPY from "~config/copy-constants"
import { uploadFileToUploadcare } from "~utils/helpers/uploadcare-helpers"

const SENTRY_ACTION_CHAT_ATTACHMENT_UPLOAD = "chat_attachment_upload"

interface Props {
    readonly onUploaded: (files: ChatUploadedFile[]) => void
}

export default function useChatPastedFileUpload({ onUploaded }: Props) {
    const { showError } = useToast()
    const [uploadingCount, setUploadingCount] = useState(0)

    const handlePastedFiles = useCallback(
        async (files: readonly File[]) => {
            const supportedFiles = files.filter((file) =>
                isSupportedAiChatAttachmentMediaType(
                    resolvePastedFileMediaType(file)
                )
            )

            if (supportedFiles.length < files.length) {
                showError(COPY.AI_CHAT_UNSUPPORTED_ATTACHMENT)
            }

            if (!supportedFiles.length) return

            setUploadingCount((count) => count + supportedFiles.length)

            const results = await Promise.allSettled(
                supportedFiles.map(uploadPastedFile)
            )

            setUploadingCount((count) => count - supportedFiles.length)

            const { uploaded, failedCount } = partitionUploadResults(results)

            if (failedCount) {
                showError(COPY.AI_CHAT_ATTACHMENT_UPLOAD_FAILED)
            }
            if (uploaded.length) onUploaded(uploaded)
        },
        [onUploaded, showError]
    )

    return { handlePastedFiles, uploadingCount }
}

// Failed uploads are already captured to Sentry by `uploadFileToUploadcare`,
// so callers only need the failure count for user feedback.
async function uploadPastedFile(file: File): Promise<ChatUploadedFile> {
    const uploadedFile = await uploadFileToUploadcare({
        file,
        sentryAction: SENTRY_ACTION_CHAT_ATTACHMENT_UPLOAD,
    })

    return {
        url: `${uploadedFile.cdnUrl}`,
        file_name: file.name || uploadedFile.name,
        mime_type: resolvePastedFileMediaType(file) || uploadedFile.mimeType,
    }
}

function partitionUploadResults(
    results: ReadonlyArray<PromiseSettledResult<ChatUploadedFile>>
) {
    const uploaded: ChatUploadedFile[] = []
    let failedCount = 0

    results.forEach((result) => {
        if (result.status === "fulfilled") {
            uploaded.push(result.value)
            return
        }

        failedCount += 1
    })

    return { uploaded, failedCount }
}
